/**
 * Driver Themes ticket — Obsidian fragment shader.
 *
 * Standalone program for the Obsidian family. Renders a dark purple
 * sphere with eight polar-symmetric droplet emitters and a four-
 * component pulse glow stack (ring + radial + bloom + base
 * gradient). The orb's silhouette picks up the background glow via
 * a fresnel bleed at the rim.
 *
 * Driven by two uniforms: uIntensity (mapped from session power kW)
 * controls droplet cadence + final brightness; uKwhScale (mapped
 * from kWh delivered) controls sphere radius + droplet count + the
 * background pulse position. The orbit angle advances with iTime
 * for ambient motion regardless of session state.
 *
 * Raymarch step count is 56 — higher than the orb composite (48)
 * because the SDF includes 8 polar repeats + smin smoothing, which
 * widens the surface region the marcher needs to resolve. Source
 * is verbatim from the ticket spec; do not refactor during
 * transcription.
 */
export const OBSIDIAN_SHADER_SOURCE = `#version 300 es
precision highp float;
uniform float iTime;
uniform vec2 iResolution;
uniform float uIntensity;
uniform float uKwhScale;
// Tap-to-grow interaction impulse (Drip). 0 at rest; each tap accumulates it
// (JS side, capped) and it decays back to 0 when tapping stops. Scaled onto the
// sphere radius below, additive on top of the uKwhScale charging growth.
uniform float uGrow;
out vec4 fragColor;
mat2 rot(float a) { return mat2(cos(a), -sin(a), sin(a), cos(a)); }
float smin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}
vec3 polarRepeat(vec3 p, float n) {
  float a = atan(p.z, p.x);
  float r = length(p.xz);
  float sector = 6.2831853 / n;
  a = mod(a + sector * 0.5, sector) - sector * 0.5;
  return vec3(cos(a) * r, p.y, sin(a) * r);
}
float hash11(float p) {
  p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p);
}
float map(vec3 p) {
  // Tap-to-grow: uGrow (0 at rest) adds to the sphere radius, so each tap grows
  // the orb and it shrinks back as the impulse decays. Additive on top of the
  // uKwhScale charging growth -- both coexist, and the droplets (which emit
  // from sphereR below) follow the grown surface.
  float sphereR = 0.30 + uKwhScale * 0.55 + uGrow * 0.25;
  float surfaceN = sin(p.x * 8.0 + iTime) * sin(p.y * 8.0 + iTime * 0.7) * sin(p.z * 8.0) * 0.02;
  float sphere = length(p) - sphereR - surfaceN;
  vec3 pp = polarRepeat(p, 8.0);
  pp.x -= sphereR;
  float armPhase = floor(atan(p.z, p.x) * 8.0 / 6.2831853 + 4.0);
  float armOffset = hash11(armPhase) * 6.28;
  float cadence = 0.4 + uIntensity * 0.4;
  float dropletCount = 1.0 + floor(uKwhScale * 3.0);
  float dist = 100.0;
  for (float i = 0.0; i < 4.0; i++) {
    if (i >= dropletCount) break;
    float t = iTime * cadence + armOffset + i * 0.45;
    float anim = fract(t);
    float wave = sin(anim * 3.14159);
    float h = -(0.4 + 0.6 * pow(wave, 0.6));
    float dropletY = -pow(anim, 0.8) * 1.7;
    float dropletR = 0.08 * wave;
    vec3 sp = pp;
    sp.y -= clamp(sp.y, h, 0.0);
    float stream = length(sp) - mix(0.025, 0.006, anim);
    vec3 dp = pp;
    dp.y -= dropletY;
    float droplet = length(dp) - dropletR;
    dist = smin(dist, stream, 0.10);
    dist = smin(dist, droplet, 0.08);
  }
  return smin(sphere, dist, 0.20);
}
vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.005, 0.0);
  return normalize(vec3(
    map(p + e.xyy) - map(p - e.xyy),
    map(p + e.yxy) - map(p - e.yxy),
    map(p + e.yyx) - map(p - e.yyx)
  ));
}
vec3 palette(float t) {
  return 0.5 + 0.5 * cos(6.2832 * (vec3(0.95, 0.50, 0.85) * t + vec3(0.0, 0.20, 0.30)));
}
void main() {
  vec2 uv = (gl_FragCoord.xy - iResolution.xy * 0.5) / iResolution.y;
  vec3 ro = vec3(0.0, 0.15, 3.4);
  vec3 target = vec3(0.0, -0.20, 0.0);
  vec3 forward = normalize(target - ro);
  vec3 right = normalize(cross(forward, vec3(0.0, 1.0, 0.0)));
  vec3 up = cross(right, forward);
  vec3 rd = normalize(uv.x * right + uv.y * up + forward * 1.7);
  float ang = iTime * 0.18;
  ro.xz *= rot(ang);
  rd.xz *= rot(ang);
  float bgDist = length(uv);
  vec3 bgBase = mix(vec3(0.06, 0.02, 0.12), vec3(0.02, 0.005, 0.04), smoothstep(0.3, 1.5, bgDist));
  float pulse = 0.35 + 0.65 * (0.5 + 0.5 * sin(iTime * 1.0));
  float ringDist = abs(bgDist - 0.78);
  vec3 ringGlow = vec3(0.85, 0.30, 1.00) * exp(-ringDist * 5.0) * 0.65 * pulse;
  vec3 radialGlow = vec3(0.70, 0.20, 0.95) * exp(-bgDist * 0.9) * 0.55 * pulse;
  vec3 bloom = vec3(0.60, 0.15, 0.85) * exp(-bgDist * 0.4) * 0.15 * pulse;
  vec3 glowAtPixel = ringGlow + radialGlow + bloom;
  vec3 col = bgBase + glowAtPixel;
  float t = 0.0;
  bool hit = false;
  for (int i = 0; i < 56; i++) {
    vec3 p = ro + rd * t;
    float d = map(p);
    if (d < 0.003) { hit = true; break; }
    if (t > 8.0) break;
    t += d * 0.85;
  }
  if (hit) {
    vec3 p = ro + rd * t;
    vec3 n = calcNormal(p);
    vec3 viewDir = -rd;
    vec3 tintHighlight = vec3(0.90, 0.45, 1.00);
    vec3 tintMid       = vec3(0.50, 0.15, 0.75);
    vec3 tintShadow    = vec3(0.12, 0.04, 0.22);
    vec3 tintSpec      = vec3(1.00, 0.80, 1.00);
    float fresnel = pow(1.0 - max(0.0, dot(n, viewDir)), 2.5);
    vec3 keyL = normalize(vec3(0.4, 1.0, -0.2));
    float keyD = max(0.0, dot(n, keyL));
    vec3 reflected = reflect(rd, n);
    float skyDot = max(0.0, reflected.y);
    float paletteT = p.y * 0.4 + iTime * 0.1;
    vec3 baseTint = palette(paletteT);
    vec3 orbCol = tintShadow * (keyD * 0.5 + 0.2);
    orbCol += tintMid * keyD * 0.5;
    orbCol += tintHighlight * pow(skyDot, 1.5) * 0.6;
    orbCol += tintHighlight * fresnel * 1.0;
    orbCol += baseTint * keyD * 0.25;
    vec3 halfway = normalize(keyL + viewDir);
    float spec = pow(max(0.0, dot(n, halfway)), 32.0);
    orbCol += tintSpec * spec * 1.2;
    orbCol += vec3(1.2, 0.40, 1.50) * pow(fresnel, 3.0);
    orbCol += glowAtPixel * fresnel * 1.4;
    col = orbCol;
  }
  col *= uIntensity * 0.6 + 0.5;
  fragColor = vec4(col, 1.0);
}`;
